/** TypeScript types for the `/gis-queue` triage surface (GIS Phase 2A, Task 10).
 *
 * MIRRORS `src/topology_platform/schemas/gis_queue.py` field-for-field — that
 * file is ground truth. Keep the two in lockstep; do not "clean up" a field
 * name or shape here without checking the Python schema first. Where the
 * Python side embeds `ImportDiscrepancyResponse` (see that class's own
 * docstring for why — it is what makes the read-closure census detect the
 * route), the TS side embeds `DiscrepancyRow` the same way rather than
 * flattening it onto `Finding`.
 */

// =============================================================================
// Findings — GET /gis-queue/findings (Task 4)
// =============================================================================

/** The 8 values `import_discrepancies.discrepancy_type` is ever written with
 *  (api/v1/gis_queue.py::DiscrepancyType). */
export type DiscrepancyType =
  | 'attribute_changed'
  | 'relationship_conflict'
  | 'relationship_ambiguous'
  | 'relationship_unresolved'
  | 'identity_conflict'
  | 'missing_from_gis'
  | 'new_in_gis'
  | 'identity_key_change'

export const DISCREPANCY_TYPES: DiscrepancyType[] = [
  'attribute_changed',
  'relationship_conflict',
  'relationship_ambiguous',
  'relationship_unresolved',
  'identity_conflict',
  'missing_from_gis',
  'new_in_gis',
  'identity_key_change',
]

/** Plain-language labels (UX-review Stage-1: no jargon-only fields), shared
 *  by the type badge, the filter dropdown, and the counts-by-type chips so
 *  the three surfaces never drift apart. */
export const DISCREPANCY_TYPE_LABELS: Record<DiscrepancyType, string> = {
  attribute_changed: 'Attribute changed',
  relationship_conflict: 'Relationship conflict',
  relationship_ambiguous: 'Relationship ambiguous',
  relationship_unresolved: 'Relationship unresolved',
  identity_conflict: 'Identity conflict',
  missing_from_gis: 'Missing from GIS',
  new_in_gis: 'New in GIS',
  identity_key_change: 'Identity key change',
}

/** The 4 `table_name` values the findings route can filter by
 *  (api/v1/gis_queue.py::TableName). */
export type TableName = 'meters' | 'transformers' | 'feeders' | 'substations'

export const TABLE_NAMES: TableName[] = ['meters', 'transformers', 'feeders', 'substations']

/** Mirrors `schemas.imports.ImportDiscrepancyResponse` verbatim (13 fields —
 *  NOT 14: the model carries `triage_batch_id` internally, but that column is
 *  deliberately not exposed on this response). */
export interface DiscrepancyRow {
  id: number
  import_run_id: number
  table_name: string
  record_id: number | null
  gis_id: string | null
  discrepancy_type: string
  field_name: string | null
  platform_value: string | null
  gis_value: string | null
  resolution: string
  resolved_at: string | null
  resolved_by: string | null
  created_at: string
}

/** Mirrors `schemas.gis_queue.FindingResponse`. NESTED, not flattened: the
 *  finding's id lives at `finding.discrepancy.id`. */
export interface Finding {
  discrepancy: DiscrepancyRow
  entity_summary: string | null
  age_days: number
}

/** Mirrors `schemas.gis_queue.FindingsListResponse`. `counts_by_type` is the
 *  filtered set EXCEPT the `type` filter itself — every OTHER active filter
 *  still constrains it, which is what makes type-tab chips work while a type
 *  filter narrows `items`/`total` to one type. */
export interface FindingsListResponse {
  items: Finding[]
  total: number
  counts_by_type: Record<string, number>
}

/** Query params for `GET /gis-queue/findings` (api/v1/gis_queue.py
 *  ::list_queue_findings). `type`/`table` are the wire names (aliased). */
export interface FindingsQuery {
  type?: DiscrepancyType
  table?: TableName
  /** One attribute of `table` (e.g. 'phase'). Free string, like the server's:
   *  field names are per-table and an unknown one matches nothing. */
  field_name?: string
  feeder_id?: number
  run_id?: number
  min_age_days?: number
  /** Three-state: undefined = any, true = fill-wave (NULL), false = overwrites. */
  platform_value_null?: boolean
  resolution?: string
  limit?: number
  offset?: number
}

// =============================================================================
// Single-row triage action — POST /gis-queue/findings/{id}/action (Task 6/7a/7b)
// =============================================================================

/** The complete, closed action vocabulary (schemas.gis_queue.FindingActionRequest
 *  ::action Literal). `adopt_gis_value` is a post-2A user-mandated addition
 *  (spec §3, 2026-07-28). */
export type FindingAction =
  | 'acknowledge'
  | 'dismiss'
  | 'resolve_identity_keep_owner'
  | 'revert_send_gis'
  | 'adopt_gis_value'
  | 'adopt_gis_parent'
  | 'assign_parent'
  | 'propose_parent'
  | 'keep_platform_relink'
  | 'confirm_retire'

export interface FindingActionRequest {
  action: FindingAction
  note?: string | null
  target_parent_id?: number | null
}

export interface FindingActionResponse {
  id: number
  action: string
  resolution: string
  resolved_by: string
  resolved_at: string
}

/** One action's complete rule set — mirrors `services.gis_queue.ACTION_SPECS`
 *  (that dict is ground truth; this is a read-only UI-side mirror used to
 *  decide which buttons render, whether a note/target is required, and
 *  which capability the button needs BEYOND the route's own `gis.triage`
 *  gate). Server enforcement remains the authority per spec §2 — this only
 *  shapes the UI. */
export interface ActionSpec {
  compatibleTypes: readonly DiscrepancyType[]
  requiresNote: boolean
  /** true for assign_parent/propose_parent only — target_parent_id REQUIRED. */
  requiresTargetParent: boolean
  /** true for adopt_gis_value only (post-2A addition): opens the inline
   *  confirm form even though no note/target is required, so the write can
   *  be stated explicitly before it happens ("Platform's <field> will
   *  change from <platform_value> to <gis_value>.") — ActionButtons.tsx
   *  renders that copy when this is true. */
  requiresValueConfirmation: boolean
  /** Human label, plain-verb per the UX-review Stage-1 language rules. */
  label: string
  /** The verb phrase that follows "Confirm " on the inline confirm-form's
   *  submit button (task-10 review L1). Deliberately separate from `label`:
   *  blindly lower-casing `label` produces "Confirm confirm retire" for
   *  `confirm_retire` (whose own label already starts with "Confirm") and
   *  lower-cases the "GIS" acronym for `propose_parent`/`keep_platform_relink`.
   *  Only actions with `requiresNote`/`requiresTargetParent` ever render this
   *  button, but every action carries a value for consistency. */
  confirmVerb: string
  /** null for Task 6's three resolution-only actions; 'gis.override' for
   *  every entity-mutating action (Task 7a/7b). */
  additionalCapability: 'gis.override' | null
}

/** Mirrors `services.gis_queue.ACTION_SPECS` field-for-field (that module's
 *  own docstring is ground truth for this table). */
export const ACTION_SPECS: Record<FindingAction, ActionSpec> = {
  acknowledge: {
    compatibleTypes: ['attribute_changed', 'new_in_gis'],
    requiresNote: false,
    requiresTargetParent: false,
    requiresValueConfirmation: false,
    label: 'Acknowledge',
    confirmVerb: 'acknowledge',
    additionalCapability: null,
  },
  dismiss: {
    compatibleTypes: ['missing_from_gis'],
    requiresNote: true,
    requiresTargetParent: false,
    requiresValueConfirmation: false,
    label: 'Dismiss',
    confirmVerb: 'dismiss',
    additionalCapability: null,
  },
  resolve_identity_keep_owner: {
    compatibleTypes: ['identity_conflict'],
    requiresNote: true,
    requiresTargetParent: false,
    requiresValueConfirmation: false,
    label: 'Resolve — keep this record',
    confirmVerb: 'resolve — keep this record',
    additionalCapability: null,
  },
  revert_send_gis: {
    // 'identity_key_change' included per spec §9 — the backend (Task 6)
    // already accepts this verb for the type; `acknowledge`/`dismiss` are
    // deliberately NOT extended here (see the docstring above ACTION_SPECS'
    // sibling entries) because they are non-mutating and would leave the
    // divergence in place, causing unbounded re-creation on the next sync.
    compatibleTypes: ['attribute_changed', 'identity_key_change'],
    requiresNote: false,
    requiresTargetParent: false,
    requiresValueConfirmation: false,
    label: 'Revert & send to GIS',
    confirmVerb: 'revert & send to GIS',
    additionalCapability: 'gis.override',
  },
  adopt_gis_value: {
    // 'identity_key_change' included per spec §9 — see revert_send_gis's
    // comment above for why acknowledge/dismiss stay excluded.
    compatibleTypes: ['attribute_changed', 'identity_key_change'],
    requiresNote: false,
    requiresTargetParent: false,
    requiresValueConfirmation: true,
    label: 'Adopt GIS value',
    confirmVerb: 'adopt GIS value',
    additionalCapability: 'gis.override',
  },
  adopt_gis_parent: {
    compatibleTypes: ['relationship_conflict'],
    requiresNote: false,
    requiresTargetParent: false,
    requiresValueConfirmation: false,
    label: 'Adopt GIS parent',
    confirmVerb: 'adopt GIS parent',
    additionalCapability: 'gis.override',
  },
  assign_parent: {
    compatibleTypes: ['relationship_ambiguous', 'relationship_unresolved'],
    requiresNote: false,
    requiresTargetParent: true,
    requiresValueConfirmation: false,
    label: 'Assign manually',
    confirmVerb: 'assign manually',
    additionalCapability: 'gis.override',
  },
  propose_parent: {
    compatibleTypes: ['relationship_ambiguous', 'relationship_unresolved'],
    requiresNote: false,
    requiresTargetParent: true,
    requiresValueConfirmation: false,
    label: 'Assign & propose to GIS',
    confirmVerb: 'assign & propose to GIS',
    additionalCapability: 'gis.override',
  },
  keep_platform_relink: {
    compatibleTypes: ['relationship_conflict'],
    requiresNote: true,
    requiresTargetParent: false,
    requiresValueConfirmation: false,
    label: 'Keep platform & ask GIS to relink',
    confirmVerb: 'keep platform & ask GIS to relink',
    additionalCapability: 'gis.override',
  },
  confirm_retire: {
    compatibleTypes: ['missing_from_gis'],
    requiresNote: true,
    requiresTargetParent: false,
    requiresValueConfirmation: false,
    label: 'Confirm retire',
    confirmVerb: 'retire',
    additionalCapability: 'gis.override',
  },
}

/** Ordered so the matrix (spec §3) renders in a stable, predictable sequence
 *  per finding type. */
export const ACTION_ORDER: FindingAction[] = [
  'acknowledge',
  'revert_send_gis',
  'adopt_gis_value',
  'adopt_gis_parent',
  'keep_platform_relink',
  'assign_parent',
  'propose_parent',
  'confirm_retire',
  'dismiss',
  'resolve_identity_keep_owner',
]

/** Actions compatible with a given finding type, in `ACTION_ORDER`. */
export function actionsForType(discrepancyType: string): FindingAction[] {
  return ACTION_ORDER.filter(action =>
    (ACTION_SPECS[action].compatibleTypes as readonly string[]).includes(discrepancyType),
  )
}

// =============================================================================
// Evidence — GET /gis-queue/findings/{id}/evidence (Task 5). Tagged union on
// `finding_type`. `new_in_gis` rows never fetch this (422 by design, spec §5).
// =============================================================================

export interface RelationshipEvidenceCandidate {
  entity_id: number
  name: string | null
  gis_ref: string | null
  distance_m: number | null
  feeder: string | null
  kva: number | null
}

export interface RelationshipEvidenceResponse {
  finding_type: 'relationship_conflict' | 'relationship_ambiguous' | 'relationship_unresolved'
  candidates: RelationshipEvidenceCandidate[]
  geojson: Record<string, unknown>
  truncated: boolean
  total_count: number
}

export interface AttributeChangedShadow {
  latest_gis_value: unknown
  latest_observed_at: string
  latest_import_run_id: number | null
}

/** AMI reading activity for a `meters`-table finding (post-2A user-mandated
 *  addition, spec §3, 2026-07-28). Evidence with a soft hint only — never an
 *  auto-action. `last_read_at`/`days_silent` are null together; `hint` is
 *  always a plain-language sentence once the lookup itself succeeded. */
export interface AmiActivityEvidence {
  last_read_at: string | null
  days_silent: number | null
  hint: string | null
}

export interface AttributeChangedEvidenceResponse {
  /** `identity_key_change` (identity-key guard, task-8) reuses this shape
   *  byte-for-byte, including `ami_activity` — the backend widened this
   *  discriminant's tag rather than adding a new evidence model
   *  (services/gis_evidence.py; see EvidencePanel.tsx's fall-through case). */
  finding_type: 'attribute_changed' | 'identity_key_change'
  platform_value: string | null
  gis_value: string | null
  source_layer: string | null
  shadow: AttributeChangedShadow | null
  /** meters-table findings only (post-2A addition); null for
   *  feeders/transformers/substations findings. See
   *  services/gis_evidence.py's module docstring for why only this shape
   *  carries it. */
  ami_activity?: AmiActivityEvidence | null
}

export interface RetirementClockState {
  qualifies: boolean
  reason: string
}

export interface MissingFromGisEvidenceResponse {
  finding_type: 'missing_from_gis'
  last_seen_run: number | null
  last_seen_generation: number | null
  alias_status: string | null
  clock: RetirementClockState
}

export interface IdentityConflictOwnerEntity {
  table_name: string
  record_id: number
  name: string | null
  gis_id: string | null
  source_layer: string | null
  source_object_id: string | null
  lon: number | null
  lat: number | null
}

export interface IdentityConflictOwnerAlias {
  id: number
  source_system: string
  source_layer: string
  normalized_global_id: string
  entity_type: string
  entity_id: number
  status: string
  first_seen_import_run_id: number | null
  last_seen_import_run_id: number | null
  note: string | null
  created_at: string
  updated_at: string
}

export interface IdentityConflictOwner {
  entity: IdentityConflictOwnerEntity | null
  alias: IdentityConflictOwnerAlias | null
}

export interface IdentityConflictClaimedTuple {
  platform_value: string | null
  gis_value: string | null
}

export interface IdentityConflictIncoming {
  gis_id: string | null
  claimed_tuple: IdentityConflictClaimedTuple
}

export interface IdentityConflictEvidenceResponse {
  finding_type: 'identity_conflict'
  owner: IdentityConflictOwner
  incoming: IdentityConflictIncoming
}

export type EvidenceResponse =
  | RelationshipEvidenceResponse
  | AttributeChangedEvidenceResponse
  | MissingFromGisEvidenceResponse
  | IdentityConflictEvidenceResponse

// =============================================================================
// Bulk triage — POST /gis-queue/bulk/preview + /bulk/execute (Task 8)
// =============================================================================

/** Mirrors `schemas.gis_queue.BulkFilterFields` — the 7 scoping fields, minus
 *  limit/offset (a bulk action always targets the entire matched set) and
 *  minus `resolution` (a bulk sweep can never be pointed at anything but
 *  pending work — the service hard-locks it to "pending"). */
export interface BulkFilterFields {
  discrepancy_type?: string | null
  table_name?: string | null
  field_name?: string | null
  feeder_id?: number | null
  run_id?: number | null
  min_age_days?: number | null
  platform_value_null?: boolean | null
}

export type BulkAction = 'acknowledge' | 'dismiss'

export interface BulkPreviewRequest {
  filter: BulkFilterFields
  action: BulkAction
  note?: string | null
}

export interface BulkExecuteRequest {
  filter: BulkFilterFields
  action: BulkAction
  note?: string | null
  filter_hash: string
}

export interface BulkAttributeChangedBreakdown {
  fills: number
  overwrites: number
}

export interface BulkPreviewResponse {
  count: number
  sample: Finding[]
  filter_hash: string
  breakdown: BulkAttributeChangedBreakdown | null
}

export interface BulkExecuteResponse {
  batch_id: number
  count: number
}

// =============================================================================
// Change requests — GET /gis-queue/crs(+/{id}) + POST /crs/{id}/withdraw (Task 9)
// =============================================================================

export type ChangeRequestKind =
  | 'attribute_fix'
  | 'relink'
  | 'new_asset'
  | 'retire_asset'
  | 'geometry_fix'
  | 'observation'

export const CR_KINDS: ChangeRequestKind[] = [
  'attribute_fix',
  'relink',
  'new_asset',
  'retire_asset',
  'geometry_fix',
  'observation',
]

/** Plain-language labels (UX-review Stage-1), same rule DISCREPANCY_TYPE_LABELS
 *  documents. */
export const CR_KIND_LABELS: Record<ChangeRequestKind, string> = {
  attribute_fix: 'Attribute fix',
  relink: 'Relink',
  new_asset: 'New asset',
  retire_asset: 'Retire asset',
  geometry_fix: 'Geometry fix',
  observation: 'Observation',
}

export type ChangeRequestStatus =
  | 'open'
  | 'submitted'
  | 'confirmed'
  | 'rejected'
  | 'conflict'
  | 'withdrawn'
  | 'superseded'

export const CR_STATUSES: ChangeRequestStatus[] = [
  'open',
  'submitted',
  'confirmed',
  'rejected',
  'conflict',
  'withdrawn',
  'superseded',
]

export const CR_STATUS_LABELS: Record<ChangeRequestStatus, string> = {
  open: 'Open',
  submitted: 'Submitted',
  confirmed: 'Confirmed',
  rejected: 'Rejected',
  conflict: 'Conflict',
  withdrawn: 'Withdrawn',
  superseded: 'Superseded',
}

/** Mirrors api/v1/gis_queue.py::ChangeRequestEntityType — the complete 14-entry
 *  vocabulary core.gis_identity.ENTITY_TYPE_FOR_TABLE.values() enumerates. */
export type ChangeRequestEntityType =
  | 'substation'
  | 'feeder'
  | 'transformer'
  | 'meter'
  | 'conductor_segment'
  | 'protective_device'
  | 'transmission_line'
  | 'underground_infrastructure_item'
  | 'critical_customer'
  | 'street_light'
  | 'pole'
  | 'secondary_conductor'
  | 'secondary_box'
  | 'service_tap'

export const CHANGE_REQUEST_ENTITY_TYPES: ChangeRequestEntityType[] = [
  'substation',
  'feeder',
  'transformer',
  'meter',
  'conductor_segment',
  'protective_device',
  'transmission_line',
  'underground_infrastructure_item',
  'critical_customer',
  'street_light',
  'pole',
  'secondary_conductor',
  'secondary_box',
  'service_tap',
]

/** Mirrors api/v1/gis_queue.py::ChangeRequestOrigin. */
export type ChangeRequestOrigin = 'gis_queue' | 'manual_reassignment' | 'secondary_review'

export const CHANGE_REQUEST_ORIGINS: ChangeRequestOrigin[] = [
  'gis_queue',
  'manual_reassignment',
  'secondary_review',
]

export interface ChangeRequestsQuery {
  kind?: ChangeRequestKind
  status?: ChangeRequestStatus
  entity_type?: ChangeRequestEntityType
  origin?: ChangeRequestOrigin
  limit?: number
  offset?: number
}

export interface ChangeRequestSummary {
  id: number
  kind: string
  status: string
  entity_type: string | null
  entity_id: number | null
  field_name: string | null
  proposed_value: unknown
  origin: string
  created_by: string
  created_at: string
  target_key: string
}

export interface ChangeRequestListResponse {
  items: ChangeRequestSummary[]
  total: number
}

export interface ChangeRequestDetail {
  id: number
  kind: string
  status: string
  entity_type: string | null
  entity_id: number | null
  gis_id: string | null
  source_layer: string | null
  target_key: string
  field_name: string | null
  proposed_value: unknown
  base_gis_value: unknown
  base_gis_value_hash: string | null
  base_captured_import_run_id: number | null
  needs_triage: boolean
  supersedes_id: number | null
  superseded_by_id: number | null
  derived_from_id: number | null
  origin: string
  created_by: string
  client_idempotency_key: string | null
  created_at: string
  updated_at: string
  notes: string | null
  confirmed_at: string | null
  confirmed_import_run_id: number | null
  match_detail: unknown
  conflict_discrepancy_id: number | null
  resolution_note: string | null
}

export interface ChangeRequestWithdrawResponse {
  id: number
  status: string
  resolution_note: string | null
  updated_at: string
}

export interface WithdrawChangeRequestRequest {
  note?: string | null
}

// =============================================================================
// Capabilities (spec §2 vocabulary; Task 2's seam) — `User.capabilities`.
// Moved to utils/capabilities.ts (UX C5, spec 3.12); re-exported so the
// gis-queue components' existing `from './types.ts'` imports keep working.
// =============================================================================

export { hasCapability, type Capability } from '../../utils/capabilities.ts'
